// eslint-disable-next-line no-restricted-imports
import { gql, useMutation, useQuery } from "@apollo/client";
import { useUser } from "@auth0/nextjs-auth0";
import { Fade } from "@material-ui/core";
import { PrimaryColor, SecondaryColor, strings } from "@resource/common";
import CompanyLogo from "components/CompanyLogo";
import ErrorBoundary from "components/legacy/ErrorBoundary";
import GuideLayout from "components/legacy/GuideLayout";
import GuideLoading from "components/legacy/GuideLoading";
import { DeprecatedStyledLayout } from "components/legacy/Layout";
import PitchPageModuleContainer from "components/legacy/modules/ModuleContainer/PitchPageModuleContainer";
import {
  TRACK_PITCH_PAGE_CLICK_EVENT,
  TRACK_PITCH_PAGE_VIEW,
} from "components/legacy/PitchPage/PITCH_PAGE";
import BasePathEnum from "enums/BasePathEnum";
import {
  FetchPitchPage,
  FetchPitchPage_pitchPageFindUnique,
  FetchPitchPageVariables,
  TrackPitchPageClickEvent,
  TrackPitchPageClickEventVariables,
  TrackPitchPageView,
  TrackPitchPageViewVariables,
} from "generated/schemaTypes";
import { GraphQLClient } from "graphql-request";
import _ from "lodash";
import { GetStaticProps, InferGetStaticPropsType } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";
import { useEffect, useState } from "react";
import { TrackContentModuleEngagementContextProvider } from "react-context/legacy/TrackContentModuleEngagementContextProvider";
import useAnalytics from "react-hooks/useAnalytics";
import { Theme } from "types/legacy";
import urljoin from "url-join";
import { captureSentryError } from "utils/errors/sentryError";
import { useQueryStringValue } from "utils/next";
import { withLayout } from "utils/next-layout";

const FETCH_PITCH_PAGE = gql`
  query FetchPitchPage($pitchPageId: UUID!) {
    pitchPageFindUnique(where: { id: $pitchPageId }) {
      id
      isSample
      recipientFirstName
      recipientLastName
      schedulerAvailabilityEvent {
        id
        schedulerAvailability {
          id
          slug
        }
        externalEditHash
      }
      activeGuide {
        id
        publicUrl
      }
      pitchPageTemplate {
        id
        createdBy {
          id
          organization {
            id
            values {
              id
              title
              description
            }
            missionStatement
            theme {
              primaryColor
              secondaryColor
            }
            ...OrganizationLogoFragment
          }
        }
        modules {
          id
          type
          position
          data
          displayType
        }
      }
    }
  }
  ${CompanyLogo.fragments.organization}
`;

export async function getStaticPaths() {
  // const recentPitchPages = await prisma.pitchPage.findMany({
  //   orderBy: {
  //     createdAt: "desc",
  //   },
  //   take: 500,
  // });

  return {
    paths: [],
    // _.map(recentPitchPages, (pitchPage) => ({
    //   params: {
    //     pitchPageId: pitchPage.id,
    //   },
    // })),
    fallback: true,
  };
}

interface StaticPropsParams extends ParsedUrlQuery {
  pitchPageId: string;
}

export const getStaticProps: GetStaticProps<
  {
    pitchPage: FetchPitchPage_pitchPageFindUnique;
  },
  StaticPropsParams
> = async ({ params }) => {
  const client = new GraphQLClient(
    urljoin(process.env.NEXT_PUBLIC_NEXTJS_APP_ROOT ?? "", "api/graphql")
  );

  if (!params) {
    return {
      notFound: true,
    };
  }
  const { pitchPageId } = params;

  const data = await client.request<FetchPitchPage, FetchPitchPageVariables>(
    FETCH_PITCH_PAGE,
    { pitchPageId }
  );

  if (!data?.pitchPageFindUnique) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      pitchPage: data.pitchPageFindUnique,
    },
  };
};

interface PitchPageProps {
  pitchPage: FetchPitchPage_pitchPageFindUnique;
}

function PitchPage({ pitchPage }: PitchPageProps) {
  const { user, isLoading } = useUser();
  const router = useRouter();
  const isAuthenticated = !!user;
  const analytics = useAnalytics();

  const [addRecipientView] = useMutation<
    TrackPitchPageView,
    TrackPitchPageViewVariables
  >(TRACK_PITCH_PAGE_VIEW);

  const [addClick] = useMutation<
    TrackPitchPageClickEvent,
    TrackPitchPageClickEventVariables
  >(TRACK_PITCH_PAGE_CLICK_EVENT);

  const trackEngagement = (attrs = {}) => {
    if (!isAuthenticated) {
      analytics.track("Pitch Page Anything Clicked", attrs);
      addClick({
        variables: {
          pitchPageId: pitchPage.id,
        },
      });
    }
  };

  useEffect(() => {
    if (isLoading || pitchPage.isSample) {
      return;
    }

    if (typeof window !== undefined && !isAuthenticated) {
      analytics.identify(pitchPage.id, {
        id: pitchPage.id,
        isPitchPageRecipient: true,
        organizationName:
          pitchPage.pitchPageTemplate.createdBy.organization.name,
        organizationId: pitchPage.pitchPageTemplate.createdBy.organization.id,
      });
    }

    analytics.page("Pitch Page Recipient");

    if (!isAuthenticated) {
      addRecipientView({
        variables: {
          pitchPageId: pitchPage.id,
        },
      });
    }

    if (pitchPage.activeGuide?.publicUrl) {
      const { pathname } = new URL(pitchPage.activeGuide.publicUrl);
      router.push(pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const { organization } = pitchPage.pitchPageTemplate.createdBy;
  const { isSample } = pitchPage;

  const trimColor = organization.theme?.primaryColor || "white";
  const theme = (
    organization.theme?.primaryColor
      ? organization.theme
      : {
          primaryColor: PrimaryColor,
          secondaryColor: SecondaryColor,
        }
  ) as Theme;

  const modules = _.sortBy(pitchPage.pitchPageTemplate.modules, "position");

  return (
    <>
      <TrackContentModuleEngagementContextProvider
        trackEngagement={trackEngagement}
      >
        <GuideLayout showShare={false} trimColor={trimColor} preview={isSample}>
          <CompanyLogo organization={organization} />
          {_.map(modules, ({ id, type: displayType, data: moduleData }) => (
            <ErrorBoundary key={id} silent>
              <PitchPageModuleContainer
                id={id}
                displayType={moduleData?.displayType ?? displayType}
                data={moduleData}
                pitchPageId={pitchPage.id}
                contextData={{
                  "candidate-first-name": pitchPage.recipientFirstName,
                  "candidate-full-name": strings.joinNames(
                    pitchPage.recipientFirstName,
                    pitchPage.recipientLastName
                  ),
                  "company-name": organization.name,
                  theme,
                }}
              />
            </ErrorBoundary>
          ))}
        </GuideLayout>
      </TrackContentModuleEngagementContextProvider>
    </>
  );
}

function PitchPageWrapper({
  pitchPage: initialPitchPage,
}: Partial<InferGetStaticPropsType<typeof getStaticProps>>) {
  const pitchPageId = useQueryStringValue("pitchPageId");
  const router = useRouter();
  const [showLoader, setShowLoader] = useState(router.isFallback);
  const [presented, setPresented] = useState(!router.isFallback);

  const { data, error } = useQuery<FetchPitchPage, FetchPitchPageVariables>(
    FETCH_PITCH_PAGE,
    {
      variables: {
        pitchPageId,
      },
      skip: !pitchPageId,
    }
  );
  const pitchPage = data?.pitchPageFindUnique || initialPitchPage;

  const organization = pitchPage?.pitchPageTemplate.createdBy.organization;

  const title = organization?.name ? `About ${organization.name}` : "Hi there!";
  const description = `Learn more about ${organization?.name || "our company"}`;
  const imageUrl =
    organization?.companyLogoUrl ||
    "https://storage.googleapis.com/remote-images-production/guide-sched-img.png";

  useEffect(() => {
    if (!router.isFallback) {
      setShowLoader(false);
    }
  }, [router.isFallback, data?.pitchPageFindUnique]);

  if (data && !pitchPage) {
    router.push(BasePathEnum[404]);
    return null;
  }

  if (error) {
    const hasNotFoundError = _.find(
      error.graphQLErrors,
      (graphQLError) => graphQLError.extensions?.code === "NOT_FOUND"
    );
    if (hasNotFoundError) {
      router.push(BasePathEnum[404]);
    }
    captureSentryError(error, "[pitchPageId] FETCH_PITCH_PAGE");
    return null;
  }

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <title>{title}</title>
        <meta key="description" name="description" content={description} />
        <meta key="ogTitle" property="og:title" content={title} />
        <meta
          key="ogDescription"
          property="og:description"
          content={description}
        />
        <meta key="ogImage" property="og:image" content={imageUrl} />
        <meta
          key="ogUrl"
          property="og:url"
          content={urljoin(
            process.env.NEXT_PUBLIC_NEXTJS_APP_ROOT ?? "",
            `/p/${pitchPageId}`
          )}
        />
        <meta key="twitterCard" name="twitter:card" content={imageUrl} />
      </Head>
      {!presented ? (
        <Fade
          in={showLoader}
          appear={false}
          unmountOnExit
          timeout={{
            exit: 500,
          }}
          onExited={() => setPresented(true)}
        >
          <div>
            <GuideLoading />
          </div>
        </Fade>
      ) : (
        <Fade in timeout={500}>
          <div>{pitchPage && <PitchPage pitchPage={pitchPage} />}</div>
        </Fade>
      )}
    </>
  );
}

export default withLayout((page) => (
  <DeprecatedStyledLayout>{page}</DeprecatedStyledLayout>
))(PitchPageWrapper);
